import React, { ReactNode } from 'react';
import TextWinds from '../../../common/TextWinds';
import { Link } from 'react-router-dom';
import {
  BookOpenIcon,
  ClipboardDocumentListIcon,
  ClipboardIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { LmsInfoTypes } from '../../../../constants/types';
import { courseIdDetermination } from '../../../../api/utils/lms/ExtendSetBtnLink';

interface QuickAccessProps {
  classId: string;
  lmsLists: LmsInfoTypes;
}

/**
 * 개편 LMS (교사) - 클램스 홈 페이지 > 페이지 이동 카드
 */
const QuickAccess = ({ classId, lmsLists }: QuickAccessProps) => {
  const courseType = courseIdDetermination(classId, lmsLists);

  return (
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-2 xl:gap-5">
      <QuickAccessCard
        title="수업자료 보기"
        to={`/lms/${classId}/take-class`}
        variant="1"
        icon={<BookOpenIcon className="h-6 w-6" />}
        children={
          <TextWinds type="content_body2" color="gray8">
            수업에 필요한 영상과 PPT 자료를 확인합니다.
          </TextWinds>
        }
      />
      <QuickAccessCard
        title="멤버십 스토어"
        to={`/lms/${classId}/membership-store`}
        variant="2"
        icon={<ShoppingCartIcon className="h-6 w-6" />}
        children={
          <TextWinds type="content_body2" color="gray8">
            코스 연장 상품 및 교재, 교구를 구매합니다.
          </TextWinds>
        }
      />
      {courseType !== 'NONE' && (
        <>
          <QuickAccessCard
            title="과제 내주기"
            to={`/lms/${classId}/assignment/create`}
            variant="3"
            icon={<ClipboardIcon className="h-6 w-6" />}
            children={
              <TextWinds type="content_body2" color="gray8">
                교재와 레슨을 선택해서 아이들에게 과제를 내줍니다.
              </TextWinds>
            }
          />
          <QuickAccessCard
            title="과제 현황보기"
            to={`/lms/${classId}/assignment/status`}
            variant="4"
            icon={<ClipboardDocumentListIcon className="h-6 w-6" />}
            children={
              <TextWinds type="content_body2" color="gray8">
                누가 과제를 완료했는지 한눈에 확인합니다.
              </TextWinds>
            }
          />
        </>
      )}
    </div>
  );
};

export default QuickAccess;

type QuickAccessCardVariant = '1' | '2' | '3' | '4';

type QuickAccessCardProps = {
  title: string;
  to: string;
  icon: ReactNode;
  variant?: QuickAccessCardVariant;
  children: ReactNode;
};

const quickAccessCardVariantMap: Record<QuickAccessCardVariant, string> = {
  '1': 'bg-[#F5F5F5] border-gray3',
  '2': 'bg-[#F5F5F5] border-gray3',
  '3': 'bg-[#F5F5F5] border-gray3',
  '4': 'bg-[#F5F5F5] border-gray3',
};

const quickAccessIconVariantMap: Record<QuickAccessCardVariant, string> = {
  '1': 'bg-[#6B5ADD] text-white',
  '2': 'bg-[#35256D] text-white',
  '3': 'bg-[#FEDB2C] text-white',
  '4': 'bg-[#FA6654] text-white',
};

const QuickAccessCard = ({
  title,
  to,
  icon,
  variant = '1',
  children,
}: QuickAccessCardProps) => (
  <Link
    to={to}
    className={[
      'group block rounded-lg border p-3 text-left shadow-card',
      'transition-all duration-200',
      'active:scale-[0.98]',
      'xl:px-8 xl:py-4 xl:hover:-translate-y-0.5 xl:hover:shadow-lg xl:active:scale-100',
      '[-webkit-tap-highlight-color:transparent]',
      quickAccessCardVariantMap[variant],
    ].join(' ')}
  >
    <div className="flex items-center justify-between xl:border-b xl:border-white/70 xl:pb-4">
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={[
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
            quickAccessIconVariantMap[variant],
          ].join(' ')}
        >
          {icon}
        </span>

        <TextWinds type="title_h2" className="!text-base xl:!text-title_h2">
          {title}
        </TextWinds>
      </div>

      <span className="hidden shrink-0 text-xs text-gray7 transition-all group-active:translate-x-0.5 xl:block xl:group-hover:translate-x-1">
        바로가기 →
      </span>
    </div>

    <div className="hidden pt-4 xl:block">{children}</div>
  </Link>
);
